/**
 * Τα έτοιμα μηνύματα κουζίνας του καταστήματος (dine_in_settings.kitchen_messages): προσθήκη,
 * σειρά, αφαίρεση. Κάθε αλλαγή αποθηκεύεται αμέσως, όπως οι διακόπτες της σελίδας· ο server
 * καθαρίζει κενά και διπλά (backend/utils/kitchenMessages.js). Κάτω από τον διακόπτη του, μέσα στην
 * ενότητα «Κουζίνα» του «Φύλλου» (13/10/2026).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, X, Plus } from 'lucide-react';
import {
  KITCHEN_MESSAGE_LIST_MAX,
  KITCHEN_MESSAGE_MAX_LENGTH,
  addPresetMessage,
  movePresetMessage,
} from '../../utils/kitchenMessages';
import { INPUT_CLS } from './storeSettingsForm';

interface KitchenMessagesEditorProps {
  messages: string[];
  onChange: (next: string[]) => void;
}

const ICON_BTN = 'w-9 h-9 shrink-0 rounded-lg flex items-center justify-center text-t3 hover:text-t1 hover:bg-surface-3 transition-colors '
  + 'disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-t3';

const KitchenMessagesEditor: React.FC<KitchenMessagesEditorProps> = ({ messages, onChange }) => {
  const { t } = useTranslation(['settings']);
  const [draft, setDraft] = useState('');
  const full = messages.length >= KITCHEN_MESSAGE_LIST_MAX;

  const add = () => {
    const next = addPresetMessage(messages, draft);
    if (next !== messages) onChange(next);
    setDraft('');
  };

  return (
    <div className="mt-5 pt-5 border-t border-dashed border-line space-y-3" data-testid="kitchen-messages-editor">
      <p className="text-sm text-t3">{t('settings:dineIn.messages.intro')}</p>

      {messages.length === 0 ? (
        <p className="text-sm text-t4">{t('settings:dineIn.messages.empty')}</p>
      ) : (
        <ul className="border-y border-line divide-y divide-line" aria-label={t('settings:dineIn.messages.listLabel')}>
          {messages.map((message, index) => (
            <li key={message} className="flex items-center gap-1 py-1 pl-1">
              <span className="flex-1 min-w-0 text-sm font-semibold text-t1 truncate">{message}</span>
              <button
                type="button"
                aria-label={t('settings:dineIn.messages.moveUp', { message })}
                disabled={index === 0}
                onClick={() => onChange(movePresetMessage(messages, index, -1))}
                className={ICON_BTN}
              >
                <ChevronUp className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('settings:dineIn.messages.moveDown', { message })}
                disabled={index === messages.length - 1}
                onClick={() => onChange(movePresetMessage(messages, index, 1))}
                className={ICON_BTN}
              >
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('settings:dineIn.messages.remove', { message })}
                onClick={() => onChange(messages.filter((_, i) => i !== index))}
                className={`${ICON_BTN} hover:text-red-600 dark:hover:text-red-400`}
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input
          value={draft}
          maxLength={KITCHEN_MESSAGE_MAX_LENGTH}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          disabled={full}
          aria-label={t('settings:dineIn.messages.newLabel')}
          placeholder={t('settings:dineIn.messages.placeholder')}
          className={`${INPUT_CLS} flex-1 min-w-0 disabled:opacity-50`}
        />
        <button
          type="button"
          onClick={add}
          disabled={full || !draft.trim()}
          className="inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 shrink-0 rounded-xl border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          {t('settings:dineIn.messages.add')}
        </button>
      </div>
      <p className="text-xs text-t4">
        {t('settings:dineIn.messages.limits', { max: KITCHEN_MESSAGE_LIST_MAX, length: KITCHEN_MESSAGE_MAX_LENGTH })}
      </p>
    </div>
  );
};

export default KitchenMessagesEditor;
